import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import classes from 'classnames';
import classNames from './TutorFromActivitise.module.scss';
import TutorAvatarIcon from '@/components/ConversationWithTutor/TutorAvatarIcon/TutorAvatarIcon';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import Chat, { ChatRef } from '@/components/ConversationWithTutor/Chat/Chat';
import {
  MessageItemProps,
  TopicProps,
  TopicsGroup,
} from '@/components/ConversationWithTutor/MessageItem/MessageItem';
import {
  TutorChatMessage,
  TutorChatSender,
  TutorChatStyle,
  TutorChatLevel,
} from '@mavatar/melingo-voice-and-avatar-sdk';
import TutorTopics from '@/utils/tutorTopics';
import { TutorType, UserState } from '@/types/user';
import { useTranslation } from 'react-i18next';
import { DispatcherEvents, EventDispatcherContext } from '@/context/eventDispatcherContext';
import SVG from 'react-inlinesvg';
import UpArrowCircle from '@/assets/svg/up-arrow-circle.svg';
import CloseConversationModal from '@/components/ConversationWithTutor/CloseConversationModal/CloseConversationModal';
import config from '@/config';

interface TutorFromActivitiseProps {
  fromCanDo?: string;
  onChatToggle?: (isOpen: boolean) => void;
  activity?: any;
  topic: any;
  level: number;
  unit: any;
}

const TutorFromActivitise = ({
  fromCanDo,
  onChatToggle,
  level,
  unit,
  topic,
  activity,
}: TutorFromActivitiseProps) => {
  const { t } = useTranslation();
  const dispatcher = useContext(EventDispatcherContext);
  const [isVisible, setIsVisible] = useState(fromCanDo ? true : false);
  const [isChatLoaded, setIsChatLoaded] = useState(fromCanDo ? true : false);
  const { user } = useAppSelector(authSelector);
  const [startMessages, setStartMessages] = useState<MessageItemProps[] | undefined>();
  const [errorTopicMessages, setErrorTopicMessages] = useState<MessageItemProps[]>([]);
  const [topicsGroups, setTopicsGroups] = useState<TopicsGroup[] | undefined>();
  const [showOnlineConversation, setShowOnlineConversation] = useState<boolean>(false);
  const chatRef = useRef<ChatRef | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState<TutorChatMessage | undefined>({
    status: 'writing',
    sender: TutorChatSender.TUTOR,
  });
  const [showPromptFromCanDoAndNotStarted, setShowPromptFromCanDoAndNotStarted] =
    useState<boolean>(false);
  const isFromCanDoAndNotStarted = fromCanDo && !showOnlineConversation;

  useEffect(() => {
    const abortController = new AbortController();
    const abortSignal = abortController.signal;

    const fetchTopics = async () => {
      if (!user) return;

      const initActivityTopics = async () => {
        const [unitTopics, topicData, activityData] = await Promise.all([
          TutorTopics.getTutorTopicsForSpecificEllUnit(user, level, unit.id),
          TutorTopics.getTutorTopicsForSpecificEllTopic(user, level, unit.id, topic.id),
          TutorTopics.getTutorTopicsForSpecificEllActivity(
            user,
            level,
            unit.id,
            topic.id,
            activity.id
          ),
        ]);

        if (abortSignal.aborted) {
          return;
        }

        const topics = [unitTopics, topicData, activityData].filter(
          topic => !!topic
        ) as TopicProps[];

        setTopicsGroups([{ topics: topics }]);

        const welcomeText = `Hi ${user?.metadata?.firstName}, Nice to meet you. I’m your private tutor. I’m here to help you improve your English. Please choose one of the following topics to talk about and we can begin!`;
        setWelcomeMessage({
          status: '',
          text: welcomeText,
          speakText: welcomeText,
          dateTime: new Date(),
          sender: TutorChatSender.TUTOR,
        });
      };

      try {
        if (!fromCanDo) {
          // initActivityTopics();
          setActivityMessage(user, level, unit, topic, abortSignal);
        } else {
          setCanDoMessage(abortSignal);
        }
      } catch (error) {
        console.error('Error fetching tutor topics:', error);
      }
    };

    if (isChatLoaded) {
      fetchTopics();
    }

    return () => {
      abortController.abort();
    };
  }, [user, level, isChatLoaded, fromCanDo, unit, topic, activity]);

  useEffect(() => {
    if (onChatToggle) {
      onChatToggle(isVisible);
    }
    if (!isVisible) {
      chatRef.current?.stopAudio();
    }
  }, [isVisible, onChatToggle]);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overscrollBehavior = 'none';
    }
    return () => {
      document.body.style.overscrollBehavior = '';
    };
  }, [isVisible]);

  const clearChat = useCallback(() => {
    setIsChatLoaded(false);
    setStartMessages(undefined);
    setErrorTopicMessages([]);
    setTopicsGroups(undefined);
    setShowOnlineConversation(false);
    chatRef.current?.stopAudio();
    setWelcomeMessage({
      status: 'writing',
      sender: TutorChatSender.TUTOR,
    });
  }, []);

  useEffect(() => {
    if (!isVisible || !config.useTutorStreaming) {
      return;
    }

    const handleOfflineClose = () => {
      clearChat();
      setIsVisible(false);
      onChatToggle?.(false);
    };

    dispatcher.addListener(DispatcherEvents.OFFLINE_POPUP_CLOSE, handleOfflineClose);

    return () => {
      dispatcher.removeListener(DispatcherEvents.OFFLINE_POPUP_CLOSE, handleOfflineClose);
    };
  }, [isVisible, clearChat, onChatToggle, dispatcher]);

  const handleMinimize = useCallback(() => {
    setIsVisible(false);
    onChatToggle?.(false);
  }, [onChatToggle]);

  const dragStartY = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleDragHandleTouchStart = useCallback((e: React.TouchEvent) => {
    try {
      const chatScrollerContent = (e.target as any)?.closest(
        '[data-contaner="ChatScrollerContent"]'
      );
      if (chatScrollerContent && chatScrollerContent !== e.target) {
        dragStartY.current = null;
        return;
      }
    } catch (ex) {}

    dragStartY.current = e.touches[0].clientY;
    if (panelRef.current) {
      panelRef.current.style.transition = 'none';
    }
  }, []);

  const handleDragHandleTouchMove = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = Math.max(0, e.touches[0].clientY - dragStartY.current);
    if (panelRef.current) {
      panelRef.current.style.transform = `translateY(${delta}px)`;
    }
  }, []);

  const handleDragHandleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (dragStartY.current === null) return;
      const delta = e.changedTouches[0].clientY - dragStartY.current;
      dragStartY.current = null;
      if (panelRef.current) {
        panelRef.current.style.transition = '';
        panelRef.current.style.transform = '';
      }
      if (delta > 30) {
        handleMinimize();
      }
    },
    [handleMinimize]
  );

  const handleDragHandleTouchCancel = useCallback(() => {
    dragStartY.current = null;
    if (panelRef.current) {
      panelRef.current.style.transition = '';
      panelRef.current.style.transform = '';
    }
  }, []);

  const handleChatToggle = (status: string) => {
    if (isFromCanDoAndNotStarted) {
      if (showPromptFromCanDoAndNotStarted) {
        setShowPromptFromCanDoAndNotStarted(false);
      } else {
        setShowPromptFromCanDoAndNotStarted(true);
        return;
      }
    }

    if (onChatToggle) {
      if (!isChatLoaded) {
        setIsChatLoaded(true);
        onChatToggle(true);
      } else if (isChatLoaded && status) {
        clearChat();
      }
      setIsVisible(prev => !prev);
      onChatToggle(!isVisible);
    }
  };

  const speechType: TutorType = useMemo(() => {
    const tutorType = user?.metadata?.organizationDetails?.tutorData?.tutorType || 'TEXT';
    return tutorType; // === 'SOUND' || tutorType === 'AVATAR' ? 'SOUND' : 'TEXT';
  }, [user]);

  const onChatError = useCallback((details: any) => {
    switch (details?.message || '') {
      case 'conversation init error: bad topic':
        setErrorTopicMessages(errorTopicMessages => {
          const newMessages = [...errorTopicMessages];

          const selectedTopicMessage = chatRef.current?.getSelectedTopicMessages() || null;
          if (selectedTopicMessage) {
            newMessages.push({
              message: selectedTopicMessage,
              voice: '',
              showSpeaker: false,
            });
          }

          const text = 'I am sorry, but this topic is not approved for conversation.';
          newMessages.push({
            message: {
              status: '',
              text: text,
              speakText: text,
              dateTime: new Date(),
              sender: TutorChatSender.TUTOR,
            },
            voice: '',
            showSpeaker: true,
            handleTopic: onTopicClick,
            topicsGroups: [
              {
                topics: [
                  {
                    style: TutorChatStyle.ROLEPLAY,
                    text: 'Back to Activity',
                    topic: '',
                    user_role: '',
                    system_role: '',
                    level: TutorChatLevel.A1,
                    tempData: 'Closed',
                  },
                ],
              },
            ],
          });

          return newMessages;
        });
        chatRef.current?.resetChat();
        break;
      default:
        break;
    }
  }, []);

  const chatComponent = useMemo(() => {
    return (
      <Chat
        welcomeMessage={welcomeMessage}
        startMessages={[
          ...(startMessages || []),
          ...(showOnlineConversation && errorTopicMessages ? errorTopicMessages : []),
        ]}
        onClose={handleChatToggle}
        speechType={speechType}
        topicsGroups={topicsGroups}
        layout='activity'
        ref={chatRef}
        enableCamera={isVisible}
        endBtnText='Back to Activity'
        onError={onChatError}
        showOnlineConversation={showOnlineConversation}
        showSelectedTopicMessages={showOnlineConversation}
        onMinimize={handleMinimize}
      />
    );
  }, [
    welcomeMessage,
    topicsGroups,
    isVisible,
    startMessages,
    errorTopicMessages,
    onChatError,
    speechType,
  ]);

  const onTopicClick = async (topic: TopicProps) => {
    if (topic.tempData === 'StartChat') {
      setStartMessages(prevMessages => {
        if (prevMessages) {
          const message: MessageItemProps = prevMessages[0];
          message.topicsGroups = undefined;
          return [message];
        }
      });
      setShowOnlineConversation(true);
    } else if (topic.tempData === 'Closed') {
      handleChatToggle('');
    }
  };

  const setCanDoMessage = async (abortSignal: AbortSignal) => {
    const firstName = user?.metadata?.firstName ?? 'there';
    const topicName = topic?.name ?? 'this topic';

    const messageText =
      fromCanDo === 'iAgree'
        ? `Hi ${firstName}, great to hear that you’re feeling confident about ${topicName}! I’m an AI assistant, let’s build on that together.`
        : fromCanDo === 'somewhatAgree'
          ? `Hi ${firstName}, it's okay to feel unsure about ${topicName}. I’m an AI assistant, and I’m here to help you go over it together and build your confidence.`
          : fromCanDo === 'iDoNotAgree'
            ? `Hi ${firstName}, sorry to hear that you’re not feeling 100% confident in the topic of ${topicName}. I’m an AI assistant and I’d like to help you by talking about it. OK?`
            : `Hi ${firstName}, let’s talk about ${topicName} and see how you're feeling about it.`;

    const message: MessageItemProps = {
      message: {
        status: 'writing',
        text: messageText,
        speakText: messageText,
        dateTime: new Date(),
        sender: TutorChatSender.TUTOR,
      },
      voice: '',
      showSpeaker: true,
      handleTopic: onTopicClick,
    };

    setStartMessages([message]);
    setWelcomeMessage(undefined);

    const topicData = await TutorTopics.getTutorTopicsForSpecificEllTopic(
      user,
      level,
      unit.id,
      topic.id
    );

    if (abortSignal.aborted) {
      return;
    }

    if (topicData) {
      const topicsGroups: TopicsGroup[] = [
        {
          topics: [
            {
              ...topicData,
              text: 'Yes, Let’s go! 🔥',
              tempData: 'StartChat',
            },
            {
              style: TutorChatStyle.ROLEPLAY,
              text: 'No',
              topic: '',
              user_role: '',
              system_role: '',
              level: TutorChatLevel.A1,
              tempData: 'Closed',
            },
          ],
        },
      ];

      message.topicsGroups = topicsGroups;
      message.message.status = '';
      setStartMessages([message]);
      chatRef.current?.setTopic(topicsGroups[0].topics[0]);
    } else {
      handleChatToggle('');
    }
  };

  const setActivityMessage = async (
    user: UserState | null,
    level: number,
    unit: any,
    topic: any,
    abortSignal: AbortSignal
  ) => {
    const [topicData] = await Promise.all([
      TutorTopics.getTutorTopicsForSpecificEllTopic(user, level, unit.id, topic.id),
    ]);

    if (abortSignal.aborted) {
      return;
    }

    const topicsGroups: TopicsGroup[] = [
      {
        topics: [],
      },
    ];

    if (topicData) {
      topicsGroups[0].topics.push({
        ...topicData,
        text: 'Okay!',
        tempData: 'StartChat',
      });
    }

    topicsGroups[0].topics.push({
      style: TutorChatStyle.ROLEPLAY,
      text: 'Back',
      topic: '',
      user_role: '',
      system_role: '',
      level: TutorChatLevel.A1,
      tempData: 'Closed',
    });

    const welcomeText = `Hi ${user?.metadata?.firstName}, thank you for calling me. I’m an AI assistant here to help you improve your English. Let's talk about ${topicData?.text}.`;

    const message: MessageItemProps = {
      message: {
        status: '',
        text: welcomeText,
        speakText: welcomeText,
        dateTime: new Date(),
        sender: TutorChatSender.TUTOR,
      },
      voice: '',
      showSpeaker: true,
      handleTopic: onTopicClick,
      topicsGroups: topicsGroups,
    };

    setStartMessages([message]);
    setWelcomeMessage(undefined);

    if (topicData) {
      chatRef.current?.setTopic(topicsGroups[0].topics[0]);
    }
  };

  return (
    <div className={classNames.tutorFromActivitise}>
      <TutorAvatarIcon
        className={classNames.chatButton}
        onClick={() => {
          handleChatToggle('');
        }}
      />
      <div className={classNames.chatButtonToolTip}>
        Hi! I’m here if you want to talk about {topic?.name || ''}!
      </div>
      <div className={classNames.mobileTutorButton} onClick={() => handleChatToggle('')}>
        <div className={classNames.mobileTutorContent}>
          <SVG src={UpArrowCircle} className={classNames.mobileTutorArrow} />
          <span className={classNames.mobileTutorLabel}>{t('privateTutor')}</span>
        </div>
      </div>
      {isChatLoaded && (
        <div
          className={classes(classNames.chatContainer, {
            [classNames.visible]: isVisible,
            [classNames.hidden]: !isVisible,
            [classNames.withAvatar]: speechType === 'AVATAR',
          })}
          data-hide_quiz_pause_modal={isVisible}
          data-block_body_scroll={isVisible}
        >
          <div
            className={classNames.chatOverlay}
            onClick={isFromCanDoAndNotStarted ? () => handleChatToggle('') : handleMinimize}
          ></div>
          <div
            className={classNames.chatContent}
            ref={panelRef}
            onTouchStart={handleDragHandleTouchStart}
            onTouchMove={handleDragHandleTouchMove}
            onTouchEnd={handleDragHandleTouchEnd}
            onTouchCancel={handleDragHandleTouchCancel}
          >
            <div className={classNames.dragHandle} />
            {chatComponent}
          </div>
        </div>
      )}

      <CloseConversationModal
        show={showPromptFromCanDoAndNotStarted}
        description={t('tutorRouterPrompt.fromCanDoBeforeStarted.title', {
          topicName: topic?.name ?? 'this topic',
        })}
        title=' '
        cancelBtnText={t('tutorRouterPrompt.fromCanDoBeforeStarted.cancelBtn') || ''}
        confirmBtnText={t('tutorRouterPrompt.fromCanDoBeforeStarted.confirmBtn') || ''}
        showBackdrop={false}
        handleConfirm={() => {
          handleChatToggle('');
        }}
        handleCancel={() => {
          setShowPromptFromCanDoAndNotStarted(false);
        }}
      />
    </div>
  );
};

export default TutorFromActivitise;
